<template>
  <fieldset class="flex" role="radiogroup">
    <div
      v-for="(style, index) in currentStarStyles"
      :key="`${name}-key-${index}`"
    >
      <input
        :id="`${name}-radio-${index}`"
        :checked="style === 'filled'"
        class="sr-only"
        inert
        type="radio"
        :value="index + 1"
      />
      <label :for="`${name}-radio-${index}`">
        <RevTooltip :modifiers position="top">
          <template #trigger="{ show, hide }">
            <Star
              :id="`${name}-star-${index}`"
              :aria-label="rateText[index]"
              class="cursor-pointer"
              role="img"
              :style
              :tabindex="getTabindex(index)"
              @click="setRate(index, hide)"
              @focusin="fillStarsToIndex(index, show)"
              @focusout="resetStars(hide)"
              @keydown.down="handleKeyPrevious(index)"
              @keydown.enter="setRate(index, hide)"
              @keydown.left="handleKeyPrevious(index)"
              @keydown.right="handleKeyNext(index)"
              @keydown.up="handleKeyNext(index)"
              @mouseleave="resetStars(hide)"
              @mouseover="fillStarsToIndex(index, show)"
            />
          </template>
          {{ rateText[index] }}
        </RevTooltip>
      </label>
    </div>
  </fieldset>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevTooltip } from '@ds/components/Tooltip'

import translations from './InteractiveRating.translations'
import Star, { type StarStyle } from './Star.vue'

const i18n = useI18n()

const rateText: string[] = [
  i18n(translations.veryBad),
  i18n(translations.bad),
  i18n(translations.notGreat),
  i18n(translations.good),
  i18n(translations.excellent),
]

const iconsStyle: StarStyle[] = [
  'outlined',
  'outlined',
  'outlined',
  'outlined',
  'outlined',
]

const modifiers = [
  {
    name: 'offset',
    options: {
      offset: [0, -3],
    },
  },
]

const props = defineProps<{
  name: string
}>()
const emit = defineEmits(['set-rate'])

const storedRate = defineModel<number>({ default: 0 })

const currentRate = ref(storedRate.value)
const currentIndex = ref<number | undefined>(undefined)

function buildStyleArrayFor(rate: number) {
  const safeRate = rate || 0

  return iconsStyle.map((_icon, index) => {
    return index < safeRate ? 'filled' : 'outlined'
  })
}

const currentStarStyles = computed(() => buildStyleArrayFor(currentRate.value))

function getTabindex(index: number) {
  const safeStoreRate = storedRate.value || 0

  // Force a focus on the first star when no rate is passed
  if (safeStoreRate === 0 && index === 0) return 0

  // Only focus the previously set rate on tab
  return safeStoreRate === index + 1 ? 0 : -1
}

function fillStarsToIndex(index: number, show: () => void) {
  // Prevent triggering this function several time while hovering the same
  // SVG icon
  if (currentIndex.value === index) return

  currentIndex.value = index
  currentRate.value = index + 1

  show()
}

function setRate(index: number, hide: () => void) {
  storedRate.value = index + 1
  emit('set-rate', rateText[index])

  hide()
}

function resetStars(hide: () => void) {
  currentIndex.value = undefined
  currentRate.value = storedRate.value

  hide()
}

function handleKeyNext(index: number) {
  if (index === 4) return

  document.getElementById(`${props.name}-star-${index}`)?.blur()
  document.getElementById(`${props.name}-star-${index + 1}`)?.focus()
}

function handleKeyPrevious(index: number) {
  if (index === 0) return

  document.getElementById(`${props.name}-star-${index}`)?.blur()
  document.getElementById(`${props.name}-star-${index - 1}`)?.focus()
}
</script>
